import {
  CurrentShipmentValues,
  ShipmentRevisionHistory,
} from "@/features/shipment-revision-history";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Copy,
  PackageSearch,
  SlidersHorizontal,
  TriangleAlert,
} from "lucide-react";

import {
  type CarrierOrder,
  type CarrierOrderShipment,
  useCarrierOrders,
} from "@/api/carrier";
import {
  ColumnsMenu,
  SelectFilter,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { formatDeliveryEta } from "@/features/delivery-eta-display";
import { PageHeader } from "@/components/page-header";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatDateTime, formatMoney } from "@/lib/format";
import { hasHttpStatus } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { operatorDisplayName } from "@/lib/operator-identity";

// Keep the complete operational desktop table available while progressively
// hiding lower-priority fields at narrower widths. The mobile card and detail
// drawer retain every field regardless of table visibility.
const RESPONSIVE: Record<string, string> = {
  dispatch: "hidden xl:table-cell",
  shipments: "hidden xl:table-cell",
  tracking: "hidden lg:table-cell",
  service: "hidden xl:table-cell",
  shipDate: "hidden xl:table-cell",
  eta: "hidden lg:table-cell",
  trip: "hidden xl:table-cell",
};

const TOGGLEABLE_COLUMNS = [
  { id: "tracking", label: "Tracking" },
  { id: "shipments", label: "Shipments" },
  { id: "service", label: "Service" },
  { id: "dispatch", label: "Dispatch center" },
  { id: "shipDate", label: "Ship date" },
  { id: "eta", label: "ETA" },
  { id: "trip", label: "Trip / operator" },
];

const STATUS_OPTIONS = [
  "PENDING_PAYMENT",
  "LABEL_READY",
  "DROPPED_AT_CARRIER",
  "PICKED_UP",
  "IN_TRANSIT",
  "AT_FACILITY",
  "OUT_FOR_DELIVERY",
  "DELIVERED",
  "EXCEPTION",
  "ON_HOLD",
  "RETURNED",
  "CANCELLED",
];

type ShipmentAddress = CarrierOrderShipment["addresses"][number];
type TaskAssignment = CarrierOrderShipment["tasks"][number];
type Operator = NonNullable<TaskAssignment["operator"]>;

function distinct<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function humanize(value: string | null | undefined): string {
  if (!value) return "-";
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function money(value: number | null | undefined, currency = "CAD"): string {
  return value === null || value === undefined
    ? "-"
    : formatMoney(value, currency);
}

function customerLabel(order: CarrierOrder): string {
  const customer = order.placerCustomer;
  const companyName = customer?.businessAccount?.companyName;
  const personName = [customer?.firstName, customer?.lastName]
    .filter(Boolean)
    .join(" ");
  if (companyName) return companyName;
  if (personName) return personName;
  return customer ? `Customer #${customer.customerNumber}` : "-";
}

function serviceName(shipment: CarrierOrderShipment): string {
  const carrier = shipment.carrier?.name ?? shipment.carrierCode ?? "-";
  const service = shipment.carrierService?.name ?? shipment.serviceCode;
  return service ? `${carrier} · ${service}` : carrier;
}

function serviceLabel(order: CarrierOrder): string {
  return distinct(order.shipments.map(serviceName)).join(", ") || "-";
}

function packageCount(order: CarrierOrder): number {
  return order.shipments.reduce((sum, s) => sum + s.packages.length, 0);
}

/** Earliest non-null ship date across shipments. */
function earliestShipDate(order: CarrierOrder): string | null {
  const dates = order.shipments
    .map((s) => s.shipDate)
    .filter(Boolean) as string[];
  if (dates.length === 0) return null;
  return dates.reduce((min, d) => (d < min ? d : min));
}

function earliestDeliveryShipment(
  order: CarrierOrder
): CarrierOrderShipment | undefined {
  return order.shipments.reduce<CarrierOrderShipment | undefined>(
    (earliest, shipment) => {
      const candidate = shipment.estimatedDelivery;
      if (!candidate) return earliest;
      if (
        !earliest?.estimatedDelivery ||
        candidate < earliest.estimatedDelivery
      )
        return shipment;
      return earliest;
    },
    undefined
  );
}

function earliestDeliveryEta(order: CarrierOrder): string {
  const shipment = earliestDeliveryShipment(order);
  return shipment ? formatDeliveryEta(shipment) : "—";
}

function orderStatuses(order: CarrierOrder): string[] {
  return distinct(order.shipments.map((s) => s.status));
}

function orderTypeLabel(order: CarrierOrder): string {
  return order.shipments.some((shipment) => shipment.pickupBookingId)
    ? "Pickup"
    : "Drop-off";
}

function shipmentCodes(order: CarrierOrder): string {
  if (order.shipments.length === 0) return "-";
  return order.shipments.map((s) => `SHP-${s.shipmentNumber}`).join(", ");
}

function trackingValues(order: CarrierOrder): string[] {
  return distinct(
    order.shipments.map((s) => s.trackingNumber).filter(Boolean) as string[]
  );
}

function dispatchCenterLabel(
  center: { name: string; code: string | null } | null | undefined
): string | null {
  if (!center) return null;
  return center.code ? `${center.name} (${center.code})` : center.name;
}

function dispatchCenterLabels(order: CarrierOrder): string[] {
  return distinct(
    order.shipments
      .flatMap((shipment) => [
        dispatchCenterLabel(shipment.pickupBooking?.dispatchCenter),
        ...shipment.tasks.map((task) =>
          dispatchCenterLabel(task.dispatchCenter)
        ),
      ])
      .filter(Boolean) as string[]
  );
}

function findAddress(
  shipment: CarrierOrderShipment,
  type: "FROM" | "TO"
): ShipmentAddress | null {
  return shipment.addresses.find((address) => address.type === type) ?? null;
}

function addressField(
  address: ShipmentAddress | null,
  field: "line1" | "line2" | "city" | "province" | "country" | "postalCode"
): string | null {
  if (!address) return null;
  if (address.address !== undefined) return address.address?.[field] ?? null;
  const physical = {
    line1: address.addressLine1,
    line2: address.addressLine2,
    city: address.addressCity,
    province: address.addressProvince,
    country: address.addressCountry,
    postalCode: address.addressPostalCode,
  };
  return physical[field];
}

function compactAddress(address: ShipmentAddress | null): string {
  const city = addressField(address, "city");
  const province = addressField(address, "province");
  const postalCode = addressField(address, "postalCode");
  const fallback = addressField(address, "line1");
  const cityLine = [city, province].filter(Boolean).join(", ");
  return cityLine || postalCode || fallback || "-";
}

function fullAddress(address: ShipmentAddress | null): string {
  const line1 = addressField(address, "line1");
  const line2 = addressField(address, "line2");
  const city = addressField(address, "city");
  const province = addressField(address, "province");
  const postalCode = addressField(address, "postalCode");
  const country = addressField(address, "country");
  const locality = [city, province, postalCode].filter(Boolean).join(", ");
  return [line1, line2, locality, country].filter(Boolean).join(", ") || "-";
}

function routeForShipment(shipment: CarrierOrderShipment): string {
  return `${compactAddress(findAddress(shipment, "FROM"))} -> ${compactAddress(
    findAddress(shipment, "TO")
  )}`;
}

function routeLabel(order: CarrierOrder): string {
  const routes = distinct(order.shipments.map(routeForShipment));
  if (routes.length === 0) return "-";
  if (routes.length === 1) return routes[0];
  return `${routes[0]} +${routes.length - 1}`;
}

function operatorLabel(operator: Operator | null | undefined): string | null {
  if (!operator) return null;
  return operatorDisplayName(operator);
}

function assignmentLabels(order: CarrierOrder): {
  trips: string[];
  operators: string[];
} {
  const trips = distinct(
    order.shipments.flatMap((shipment) =>
      shipment.tasks
        .map((task) => (task.trip ? `Trip #${task.trip.tripNumber}` : null))
        .filter(Boolean)
    ) as string[]
  );
  const operators = distinct(
    order.shipments.flatMap((shipment) =>
      shipment.tasks
        .flatMap((task) => [
          operatorLabel(task.operator),
          operatorLabel(task.trip?.operator),
        ])
        .filter(Boolean)
    ) as string[]
  );
  return { trips, operators };
}

function assignmentLabel(order: CarrierOrder): string {
  const { trips, operators } = assignmentLabels(order);
  if (trips.length === 0 && operators.length === 0) return "Unassigned";
  if (trips.length > 0 && operators.length > 0) {
    return `${trips.join(", ")} · ${operators.join(", ")}`;
  }
  return [...trips, ...operators].join(", ");
}

function AddressDetail({
  label,
  address,
}: {
  label: string;
  address: ShipmentAddress | null;
}) {
  return (
    <div className="min-w-0 rounded-md bg-muted/30 p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm">{fullAddress(address)}</p>
      <div className="mt-2 space-y-0.5 break-words text-xs text-muted-foreground">
        <p>
          {[address?.contactName, address?.contactCompany]
            .filter(Boolean)
            .join(" · ") || "No contact name"}
        </p>
        <p>{address?.contactPhone ?? "No contact phone"}</p>
        <p>{address?.contactEmail ?? "No contact email"}</p>
      </div>
    </div>
  );
}

function ShipmentDetailCard({ shipment }: { shipment: CarrierOrderShipment }) {
  const fromAddress = findAddress(shipment, "FROM");
  const toAddress = findAddress(shipment, "TO");
  const center =
    dispatchCenterLabel(shipment.pickupBooking?.dispatchCenter) ??
    dispatchCenterLabel(shipment.tasks[0]?.dispatchCenter) ??
    "-";
  const shipmentRecord = shipment.relatedRecords?.shipment;

  return (
    <article className="min-w-0 rounded-md border p-3 text-sm sm:p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs font-medium">
            SHP-{shipment.shipmentNumber}
          </div>
          <div className="mt-1 break-words text-xs text-muted-foreground">
            {serviceName(shipment)}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {shipmentRecord && (
            <Button asChild variant="outline" size="sm-touch">
              <Link to={`/shipments?shipmentId=${shipmentRecord.id}`}>
                Open Shipment
              </Link>
            </Button>
          )}
          <StatusBadge status={shipment.status} />
        </div>
      </div>

      <dl className="grid grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-x-4 gap-y-2 text-xs">
        <dt className="text-muted-foreground">Tracking</dt>
        <dd className="break-all font-mono">
          {shipment.trackingNumber ?? "-"}
        </dd>
        <dt className="text-muted-foreground">Carrier tracking</dt>
        <dd className="break-all font-mono">
          {shipment.carrierTrackingNumber ?? "-"}
        </dd>
        <dt className="text-muted-foreground">Master tracking</dt>
        <dd className="break-all font-mono">
          {shipment.masterTracking ?? "-"}
        </dd>
        <dt className="text-muted-foreground">Provider reference</dt>
        <dd className="break-all font-mono">{shipment.providerRef ?? "-"}</dd>
        <dt className="text-muted-foreground">Tracking page</dt>
        <dd className="min-w-0 break-all">
          {shipment.trackingUrl ? (
            <a
              href={shipment.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              Open tracking page
            </a>
          ) : (
            "-"
          )}
        </dd>
        <dt className="text-muted-foreground">Carrier</dt>
        <dd className="break-words">{serviceName(shipment)}</dd>
        <dt className="text-muted-foreground">Carrier code</dt>
        <dd>{shipment.finalCarrierCode ?? shipment.carrierCode ?? "-"}</dd>
        <dt className="text-muted-foreground">Service code</dt>
        <dd>{shipment.carrierService?.code ?? shipment.serviceCode ?? "-"}</dd>
        <dt className="text-muted-foreground">Package code</dt>
        <dd>{shipment.packageCode ?? "-"}</dd>
        <dt className="text-muted-foreground">Pickup booking</dt>
        <dd className="break-all">{shipment.pickupBookingId ?? "-"}</dd>
        <dt className="text-muted-foreground">Dispatch center</dt>
        <dd className="break-words">{center}</dd>
        <dt className="text-muted-foreground">Ship date</dt>
        <dd>{formatDate(shipment.shipDate)}</dd>
        <dt className="text-muted-foreground">Requested delivery</dt>
        <dd>{formatDate(shipment.deliveryDate)}</dd>
        <dt className="text-muted-foreground">ETA</dt>
        <dd>{formatDeliveryEta(shipment)}</dd>
        <dt className="text-muted-foreground">Delivered</dt>
        <dd>{formatDate(shipment.actualDelivery)}</dd>
        <dt className="text-muted-foreground">Service completed</dt>
        <dd>{formatDateTime(shipment.serviceCompletedAt)}</dd>
        <dt className="text-muted-foreground">Current shared visit date</dt>
        <dd>{formatDate(shipment.pickupBooking?.scheduledDate)}</dd>
        <dt className="text-muted-foreground">Current shared visit window</dt>
        <dd>
          {shipment.pickupBooking?.slot
            ? `${shipment.pickupBooking.slot.name} · ${shipment.pickupBooking.slot.startTime}–${shipment.pickupBooking.slot.endTime}`
            : "-"}
        </dd>
        <dt className="text-muted-foreground">Packages</dt>
        <dd>{shipment.packages.length}</dd>
      </dl>

      <p className="mt-3 text-xs text-muted-foreground">
        Current shared visit details may change for remaining work. They do not
        describe a completed pickup or prove this Shipment adopted a later
        visit.
      </p>
      <CurrentShipmentValues shipment={shipment} />
      <ShipmentRevisionHistory shipmentId={shipment.id} />
      <div className="mt-4 border-t pt-4">
        <p className="text-xs font-medium text-muted-foreground">
          Shipment financials · {shipment.currency}
        </p>
        <dl className="mt-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1.5 text-xs">
          <dt className="text-muted-foreground">Sell amount</dt>
          <dd className="text-right">
            {money(shipment.sellAmountCents, shipment.currency)}
          </dd>
          <dt className="text-muted-foreground">Pickup price</dt>
          <dd className="text-right">
            {money(shipment.pickupPriceCents, shipment.currency)}
          </dd>
          <dt className="text-muted-foreground">Delivery fee</dt>
          <dd className="text-right">
            {money(shipment.pricing?.deliveryFeeCents, shipment.currency)}
          </dd>
          <dt className="text-muted-foreground">Pickup fee</dt>
          <dd className="text-right">
            {money(shipment.pricing?.pickupFeeCents, shipment.currency)}
          </dd>
          <dt className="text-muted-foreground">Pickup discount</dt>
          <dd className="text-right">
            {money(
              shipment.pricing?.pickupBundleDiscountCents,
              shipment.currency
            )}
          </dd>
          <dt className="text-muted-foreground">Subtotal</dt>
          <dd className="text-right">
            {money(shipment.pricing?.subtotalCents, shipment.currency)}
          </dd>
          <dt className="text-muted-foreground">Tax</dt>
          <dd className="text-right">
            {money(shipment.pricing?.taxCents, shipment.currency)}
          </dd>
          <dt className="font-medium">Shipment total</dt>
          <dd className="text-right font-semibold">
            {money(shipment.pricing?.totalCents, shipment.currency)}
          </dd>
        </dl>
      </div>

      <div className="mt-4 grid gap-2 border-t pt-4 sm:grid-cols-2">
        <AddressDetail label="From" address={fromAddress} />
        <AddressDetail label="To" address={toAddress} />
      </div>

      <div className="mt-4 border-t pt-4">
        <p className="text-xs font-medium text-muted-foreground">
          Packages ({shipment.packages.length})
        </p>
        {shipment.packages.length === 0 ? (
          <p className="mt-2 text-sm">No packages</p>
        ) : (
          <div className="mt-2 space-y-2">
            {shipment.packages.map((item, index) => (
              <div
                key={item.id}
                className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 rounded-md bg-muted/30 p-2 text-xs"
              >
                <span className="font-medium">Package {index + 1}</span>
                <span className="break-all text-right font-mono text-muted-foreground">
                  {item.individualTrackingNumber ?? "No individual tracking"}
                </span>
                <span className="text-muted-foreground">Weight</span>
                <span className="text-right">
                  {item.weightGrams === null
                    ? "Not provided"
                    : `${item.weightGrams.toLocaleString("en-CA")} g`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 border-t pt-4">
        <p className="text-xs font-medium text-muted-foreground">
          Dispatch assignment
        </p>
        {shipment.tasks.length === 0 ? (
          <p className="mt-2 text-sm">Unassigned</p>
        ) : (
          <div className="mt-2 space-y-2">
            {shipment.tasks.map((task) => (
              <div
                key={task.id}
                className="grid gap-1 rounded-md bg-muted/40 p-3 text-xs md:grid-cols-[1fr_auto]"
              >
                <div className="min-w-0">
                  <span className="font-medium">
                    Task #{task.taskNumber} · {humanize(task.type)}
                  </span>
                  <div className="mt-1 break-words text-muted-foreground">
                    {task.trip ? `Trip #${task.trip.tripNumber}` : "No trip"} ·{" "}
                    {operatorLabel(task.operator) ??
                      operatorLabel(task.trip?.operator) ??
                      "No operator"}
                  </div>
                  <div className="mt-1 break-words text-muted-foreground">
                    {dispatchCenterLabel(task.dispatchCenter) ??
                      "No dispatch center"}
                    {task.trip ? ` · Trip ${humanize(task.trip.status)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 md:justify-end">
                  {shipment.relatedRecords?.tasks.some(
                    (record) => record.id === task.id
                  ) && (
                    <Button asChild variant="outline" size="sm-touch">
                      <Link to={`/tasks?taskId=${task.id}`}>Open Task</Link>
                    </Button>
                  )}
                  {task.trip &&
                    shipment.relatedRecords?.trips.some(
                      (record) => record.id === task.trip?.id
                    ) && (
                      <Button asChild variant="outline" size="sm-touch">
                        <Link to={`/trips?tripId=${task.trip.id}`}>
                          Open Trip
                        </Link>
                      </Button>
                    )}
                  <StatusBadge status={task.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function OrderDetailDrawer({
  order,
  open,
  onOpenChange,
}: {
  order: CarrierOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // `order` is retained while the drawer slides out so content doesn't blank
  // during the close animation; `open` alone drives visibility.
  const customer = order ? customerLabel(order) : null;
  const tracking = order ? trackingValues(order) : [];
  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={order ? `Order ORD-${order.orderNumber}` : ""}
      description={customer && customer !== "-" ? customer : undefined}
      widthClassName="sm:max-w-xl"
    >
      {order && (
        <div className="space-y-5">
          <section>
            <h3 className="text-sm font-semibold">Order information</h3>
            <dl className="mt-3 grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Order number</dt>
              <dd className="break-all font-mono">ORD-{order.orderNumber}</dd>
              <dt className="text-muted-foreground">Order type</dt>
              <dd>{orderTypeLabel(order)}</dd>
              <dt className="text-muted-foreground">Customer</dt>
              <dd className="break-words">{customerLabel(order)}</dd>
              <dt className="text-muted-foreground">Customer number</dt>
              <dd>{order.placerCustomer?.customerNumber ?? "-"}</dd>
              <dt className="text-muted-foreground">Customer email</dt>
              <dd className="break-all">
                {order.placerCustomer?.email ?? "-"}
              </dd>
              <dt className="text-muted-foreground">Customer phone</dt>
              <dd className="break-all">
                {order.placerCustomer?.phone ?? "-"}
              </dd>
              <dt className="text-muted-foreground">Route</dt>
              <dd className="break-words">{routeLabel(order)}</dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatDateTime(order.createdAt)}</dd>
              <dt className="text-muted-foreground">Updated</dt>
              <dd>{formatDateTime(order.updatedAt)}</dd>
              <dt className="text-muted-foreground">Shipments</dt>
              <dd className="break-words">{shipmentCodes(order)}</dd>
              <dt className="text-muted-foreground">Packages</dt>
              <dd>{packageCount(order)}</dd>
              <dt className="text-muted-foreground">Ship date</dt>
              <dd>{formatDate(earliestShipDate(order))}</dd>
              <dt className="text-muted-foreground">Earliest ETA</dt>
              <dd>{earliestDeliveryEta(order)}</dd>
              <dt className="text-muted-foreground">Statuses</dt>
              <dd className="flex flex-wrap gap-1">
                {orderStatuses(order).map((status) => (
                  <StatusBadge key={status} status={status} />
                ))}
              </dd>
              <dt className="text-muted-foreground">Tracking</dt>
              <dd className="flex min-w-0 items-start gap-1">
                <span className="min-w-0 break-all font-mono text-xs">
                  {tracking.join(", ") || "-"}
                </span>
                {tracking.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs-touch"
                    className="-my-2 shrink-0"
                    aria-label={`Copy tracking for ORD-${order.orderNumber}`}
                    onClick={() => {
                      void navigator.clipboard?.writeText(tracking.join(", "));
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                )}
              </dd>
              <dt className="text-muted-foreground">Dispatch centers</dt>
              <dd className="break-words">
                {dispatchCenterLabels(order).join(", ") || "-"}
              </dd>
              <dt className="text-muted-foreground">Trip / operator</dt>
              <dd className="break-words">{assignmentLabel(order)}</dd>
            </dl>
          </section>

          <section className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold">Financials and references</h3>
            {order.financialScope === "PARTIAL_ORDER" && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                Order totals are hidden because this carrier can only see part
                of the Order. Shipment financials below remain carrier-scoped.
              </p>
            )}
            <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Financial scope</dt>
              <dd>{humanize(order.financialScope)}</dd>
              <dt className="text-muted-foreground">Currency</dt>
              <dd>{order.currency}</dd>
              <dt className="text-muted-foreground">Subtotal</dt>
              <dd>{money(order.subtotalCents, order.currency)}</dd>
              <dt className="text-muted-foreground">Tax</dt>
              <dd>{money(order.taxCents, order.currency)}</dd>
              <dt className="font-medium">Order total</dt>
              <dd className="font-semibold">
                {money(order.totalCents, order.currency)}
              </dd>
              <dt className="text-muted-foreground">Order ID</dt>
              <dd className="break-all font-mono text-xs">
                {order.references?.orderId ?? order.id}
              </dd>
              <dt className="text-muted-foreground">Legacy Order ID</dt>
              <dd className="break-all font-mono text-xs">
                {order.references?.legacyOrderId ?? "-"}
              </dd>
              <dt className="text-muted-foreground">Legacy Trip ID</dt>
              <dd className="break-all font-mono text-xs">
                {order.references?.legacyTripId ?? "-"}
              </dd>
            </dl>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">
              Shipments ({order.shipments.length})
            </h3>
            {order.shipments.map((shipment) => (
              <ShipmentDetailCard key={shipment.id} shipment={shipment} />
            ))}
          </section>
        </div>
      )}
    </DetailDrawer>
  );
}

function OrderMobileCard({
  order,
  onOpen,
}: {
  order: CarrierOrder;
  onOpen: () => void;
}) {
  const statuses = orderStatuses(order);
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto min-h-11 w-full items-stretch justify-start whitespace-normal rounded-md border bg-background p-3 text-left hover:bg-muted/40"
      aria-label={`View order ORD-${order.orderNumber}`}
      onClick={onOpen}
    >
      <span className="min-w-0 flex-1 space-y-2">
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0">
            <span className="block font-mono text-xs font-semibold">
              ORD-{order.orderNumber}
            </span>
            <span className="mt-0.5 block break-words text-xs text-muted-foreground">
              {customerLabel(order)}
            </span>
          </span>
          <span className="flex shrink-0 flex-wrap justify-end gap-1">
            {statuses.length === 0 ? (
              <span className="text-xs text-muted-foreground">No status</span>
            ) : (
              statuses.map((status) => (
                <StatusBadge key={status} status={status} />
              ))
            )}
          </span>
        </span>
        <span className="block break-words text-xs">{routeLabel(order)}</span>
        <span className="block break-words text-[11px] text-muted-foreground">
          {distinct(order.shipments.map(serviceName)).join(", ") ||
            "No service"}
        </span>
        <span className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span>{formatDate(earliestShipDate(order))}</span>
          <span>
            {orderTypeLabel(order)} · {order.shipments.length} shipment
            {order.shipments.length === 1 ? "" : "s"}
          </span>
          <span>{packageCount(order)} packages</span>
        </span>
      </span>
    </Button>
  );
}

function OrdersLoading() {
  return (
    <div role="status" aria-label="Loading orders" className="space-y-2 p-3">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
  );
}

function OrdersMessage({
  kind,
  filtered,
  onRetry,
}: {
  kind: "empty" | "error" | "forbidden";
  filtered?: boolean;
  onRetry?: () => void;
}) {
  const message =
    kind === "forbidden"
      ? "You do not have permission to view carrier orders."
      : kind === "error"
        ? "Orders could not be loaded."
        : filtered
          ? "No orders match the current filters."
          : "No carrier orders yet.";
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      {kind === "empty" ? (
        <PackageSearch className="h-7 w-7 text-muted-foreground" />
      ) : (
        <TriangleAlert className="h-7 w-7 text-destructive" />
      )}
      <p role={kind === "empty" ? undefined : "alert"} className="text-sm">
        {message}
      </p>
      {kind === "error" && onRetry && (
        <Button type="button" variant="outline" size="touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function OrdersFilterFields({
  customerEmail,
  type,
  status,
  mobile = false,
  onCustomerEmailChange,
  onTypeChange,
  onStatusChange,
}: {
  customerEmail: string;
  type: "ALL" | "PICKUP" | "DROPOFF";
  status: string;
  mobile?: boolean;
  onCustomerEmailChange: (value: string) => void;
  onTypeChange: (value: "ALL" | "PICKUP" | "DROPOFF") => void;
  onStatusChange: (value: string) => void;
}) {
  return (
    <div className={mobile ? "space-y-4" : "flex items-center gap-2"}>
      <div className={mobile ? "space-y-1" : undefined}>
        {mobile && (
          <label htmlFor="order-customer-email" className="text-sm font-medium">
            Customer email
          </label>
        )}
        <Input
          id={mobile ? "order-customer-email" : undefined}
          aria-label="Filter by customer email"
          className={mobile ? "w-full" : "w-56"}
          placeholder="customer@email.com"
          value={customerEmail}
          onChange={(event) => onCustomerEmailChange(event.target.value)}
        />
      </div>
      <div className={mobile ? "space-y-1" : undefined}>
        {mobile && <p className="text-sm font-medium">Shipment type</p>}
        {mobile ? (
          <NativeSelect
            aria-label="Filter by shipment type"
            value={type}
            onChange={(event) =>
              onTypeChange(event.target.value as typeof type)
            }
            className="w-full"
          >
            <option value="ALL">All types</option>
            <option value="PICKUP">Pickup</option>
            <option value="DROPOFF">Drop-off</option>
          </NativeSelect>
        ) : (
          <SelectFilter
            ariaLabel="Filter by shipment type"
            value={type}
            onValueChange={onTypeChange}
            options={[
              { value: "ALL", label: "All types" },
              { value: "PICKUP", label: "Pickup" },
              { value: "DROPOFF", label: "Drop-off" },
            ]}
          />
        )}
      </div>
      <div className={mobile ? "space-y-1" : undefined}>
        {mobile && <p className="text-sm font-medium">Shipment status</p>}
        {mobile ? (
          <NativeSelect
            aria-label="Filter by shipment status"
            value={status}
            onChange={(event) => onStatusChange(event.target.value)}
            className="w-full"
          >
            <option value="ALL">All statuses</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {humanize(option)}
              </option>
            ))}
          </NativeSelect>
        ) : (
          <SelectFilter
            ariaLabel="Filter by shipment status"
            value={status}
            onValueChange={onStatusChange}
            options={[
              { value: "ALL", label: "All statuses" },
              ...STATUS_OPTIONS.map((option) => ({
                value: option,
                label: humanize(option),
              })),
            ]}
          />
        )}
      </div>
    </div>
  );
}

export default function OrdersScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedOrderId = searchParams.get("orderId");
  const { page, pageSize, search, setPage, setPageSize, setSearch } =
    usePagedState({ initialSearch: searchParams.get("q") ?? "" });
  const [status, setStatus] = useState("ALL");
  const [type, setType] = useState<"ALL" | "PICKUP" | "DROPOFF">("ALL");
  const [customerEmailInput, setCustomerEmailInput] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersTriggerRef = useRef<HTMLButtonElement>(null);
  const customerEmail = useDebouncedValue(customerEmailInput);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(
    () => linkedOrderId
  );
  const { data, isLoading, error, refetch } = useCarrierOrders({
    page,
    limit: pageSize,
    search,
    status,
    type,
    customerEmail,
    ...(linkedOrderId && { orderId: linkedOrderId }),
    refreshCurrent: !!detailOrderId,
  });
  const { isVisible, toggle: toggleColumn } = useColumnVisibility(
    "carrier-web:columns:orders"
  );

  const openDetail = (order: CarrierOrder) => {
    setDetailOrderId(order.id);
  };

  const rows = data?.data ?? [];
  const detail = rows.find((order) => order.id === detailOrderId) ?? null;
  const filtered = Boolean(
    search.trim() || customerEmail.trim() || status !== "ALL" || type !== "ALL"
  );
  const activeFilterCount =
    Number(Boolean(customerEmailInput.trim())) +
    Number(status !== "ALL") +
    Number(type !== "ALL");
  const blockingError = error && rows.length === 0;
  const colClass = (id: string) =>
    isVisible(id) ? (RESPONSIVE[id] ?? "") : "hidden";

  return (
    <div className="carrier-page">
      <PageHeader title="Orders" />

      <Card className="overflow-hidden">
        <div className="border-b p-4">
          <TableToolbar
            className="mb-0"
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Order # or tracking..."
            searchAriaLabel="Search by order number or tracking"
          >
            <div className="hidden sm:block">
              <OrdersFilterFields
                customerEmail={customerEmailInput}
                type={type}
                status={status}
                onCustomerEmailChange={(value) => {
                  setPage(1);
                  setCustomerEmailInput(value);
                }}
                onTypeChange={(value) => {
                  setPage(1);
                  setType(value);
                }}
                onStatusChange={(value) => {
                  setPage(1);
                  setStatus(value);
                }}
              />
            </div>
            <div className="hidden xl:block">
              <ColumnsMenu
                columns={TOGGLEABLE_COLUMNS}
                isVisible={isVisible}
                onToggle={toggleColumn}
              />
            </div>
            <Button
              ref={filtersTriggerRef}
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto xl:hidden"
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
          </TableToolbar>
        </div>

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent
            side="right"
            className="flex w-full flex-col p-0 sm:max-w-sm"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              filtersTriggerRef.current?.focus();
            }}
          >
            <SheetHeader className="border-b px-4 py-4 pr-12 text-left">
              <SheetTitle>Order filters</SheetTitle>
              <SheetDescription>
                Narrow the carrier-visible order list.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4">
              <OrdersFilterFields
                mobile
                customerEmail={customerEmailInput}
                type={type}
                status={status}
                onCustomerEmailChange={(value) => {
                  setPage(1);
                  setCustomerEmailInput(value);
                }}
                onTypeChange={(value) => {
                  setPage(1);
                  setType(value);
                }}
                onStatusChange={(value) => {
                  setPage(1);
                  setStatus(value);
                }}
              />
              <section className="mt-6 border-t pt-5">
                <h3 className="text-sm font-semibold">Desktop columns</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Choose which optional columns appear when you return to the
                  desktop table.
                </p>
                <div className="mt-3 space-y-1">
                  {TOGGLEABLE_COLUMNS.map((column) => (
                    <label
                      key={column.id}
                      htmlFor={`order-column-${column.id}`}
                      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 text-sm hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`order-column-${column.id}`}
                        checked={isVisible(column.id)}
                        onCheckedChange={() => toggleColumn(column.id)}
                        aria-label={`Show ${column.label} in desktop table`}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </section>
            </div>
            <div className="grid grid-cols-2 gap-2 border-t p-4">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={activeFilterCount === 0}
                onClick={() => {
                  setCustomerEmailInput("");
                  setType("ALL");
                  setStatus("ALL");
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
              <Button
                type="button"
                size="touch"
                onClick={() => setFiltersOpen(false)}
              >
                Show orders
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {error && rows.length > 0 && (
          <div
            role="alert"
            className="flex flex-col gap-2 border-b border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
          >
            <span>Order refresh failed. Loaded results remain available.</span>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="bg-background"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <OrdersLoading />
        ) : blockingError ? (
          <OrdersMessage
            kind={hasHttpStatus(error, 403) ? "forbidden" : "error"}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <OrdersMessage kind="empty" filtered={filtered} />
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((order) => (
                <OrderMobileCard
                  key={order.id}
                  order={order}
                  onOpen={() => openDetail(order)}
                />
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Order</TableHead>
                    <TableHead className="whitespace-nowrap">
                      Customer
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Route</TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("dispatch"))}
                    >
                      Dispatch center
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("shipments"))}
                    >
                      Shipments
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("tracking"))}
                    >
                      Tracking
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("service"))}
                    >
                      Service
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("shipDate"))}
                    >
                      Ship date
                    </TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("eta"))}
                    >
                      ETA
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Pkgs
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead
                      className={cn("whitespace-nowrap", colClass("trip"))}
                    >
                      Trip / operator
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((order) => {
                    const tracking = trackingValues(order);
                    return (
                      <TableRow
                        key={order.id}
                        className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                        tabIndex={0}
                        aria-label={`View order ORD-${order.orderNumber}`}
                        onClick={() => openDetail(order)}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openDetail(order);
                          }
                        }}
                      >
                        <TableCell>
                          <div className="font-mono text-xs">
                            ORD-{order.orderNumber}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {orderTypeLabel(order)} · {order.shipments.length}{" "}
                            shipment
                            {order.shipments.length === 1 ? "" : "s"}
                          </div>
                        </TableCell>
                        <TableCell>{customerLabel(order)}</TableCell>
                        <TableCell className="max-w-48 text-xs">
                          {routeLabel(order)}
                        </TableCell>
                        <TableCell
                          className={cn("text-xs", colClass("dispatch"))}
                        >
                          {dispatchCenterLabels(order).join(", ") || "-"}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "font-mono text-xs",
                            colClass("shipments")
                          )}
                        >
                          {shipmentCodes(order)}
                        </TableCell>
                        <TableCell
                          className={cn("min-w-36", colClass("tracking"))}
                        >
                          <div className="flex items-center gap-1">
                            <span className="max-w-[10rem] truncate font-mono text-xs">
                              {tracking.length > 0 ? tracking.join(", ") : "-"}
                            </span>
                            {tracking.length > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`Copy tracking for ORD-${order.orderNumber}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void navigator.clipboard?.writeText(
                                    tracking.join(", ")
                                  );
                                }}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={colClass("service")}>
                          {serviceLabel(order)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "whitespace-nowrap",
                            colClass("shipDate")
                          )}
                        >
                          {formatDate(earliestShipDate(order))}
                        </TableCell>
                        <TableCell
                          className={cn("whitespace-nowrap", colClass("eta"))}
                        >
                          {earliestDeliveryEta(order)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          {packageCount(order)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {orderStatuses(order).length === 0
                              ? "-"
                              : orderStatuses(order).map((status) => (
                                  <StatusBadge key={status} status={status} />
                                ))}
                          </div>
                        </TableCell>
                        <TableCell className={cn("text-xs", colClass("trip"))}>
                          {assignmentLabel(order)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={data.metadata.total}
              totalPages={data.metadata.totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      <OrderDetailDrawer
        order={detail}
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailOrderId(null);
            if (searchParams.has("orderId")) {
              const next = new URLSearchParams(searchParams);
              next.delete("orderId");
              setSearchParams(next, { replace: true });
            }
          }
        }}
      />
    </div>
  );
}
